<script setup lang="ts">
import { use_client_detail } from "@/components/ClientDetail/useClientDetail";
import ClientFunctionCatalog from "@/components/ClientDetail/ClientDetailNavigation/ClientFunctionCatalog.vue";
import { clientFunctionCategories, functionLabels } from "@/components/ClientDetail/ClientDetailNavigation/functionCategories";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

const store = use_client_detail();
const router = useRouter();
const route = useRoute();
const searchInput = ref<{ focus: () => void } | null>(null);
const category = computed(() => clientFunctionCategories.find((item) => item.id === route.query.category) ?? clientFunctionCategories[0]);
const permission = computed(() => store.client_detail?.privileges?.permission?.trim().toLowerCase());
// ClientDetail còn đang tải /agent/:id: chưa biết quyền thì chưa khẳng định thiếu quyền.
const privilegeLoaded = computed(() => store.client_detail !== undefined);
// These functions require Administrator/SYSTEM on the agent.
const adminPermissionInsufficient = computed(() => privilegeLoaded.value && permission.value !== "administrator" && permission.value !== "system");
const needsAdmin = (route: string) => ["ClientFindDML", "ClientKillAV", "ClientBrowserCredential"].includes(route);
const PERMISSION_REASON = "Yêu cầu quyền Administrator hoặc SYSTEM trên agent";
// These three existing routes share the gathering entry in the store.
const gatheringRoutes = ["ClientRecon", "ClientNmap", "ClientBruteForce"];
const items = computed(() => category.value.routes.flatMap((route) => {
  const tab = store.tabs.find((item) => item.route_name === (gatheringRoutes.includes(route) ? "ClientGathering" : route));
  if (!tab || tab.disabled) return [];
  const insufficient = needsAdmin(route) && adminPermissionInsufficient.value;
  return [{
    id: route,
    label: functionLabels[route] ?? tab.value,
    keywords: tab.keywords,
    icon: tab.ico,
    disabled: insufficient,
    reason: insufficient ? PERMISSION_REASON : undefined,
  }];
}));

function focusSearch(event: KeyboardEvent) {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "f") {
    event.preventDefault();
    searchInput.value?.focus();
  }
}
onMounted(() => window.addEventListener("keydown", focusSearch));
onBeforeUnmount(() => window.removeEventListener("keydown", focusSearch));

function openFunction(id: string) {
  const tab = store.tabs.find((item) => item.route_name === (gatheringRoutes.includes(id) ? "ClientGathering" : id));
  if (!tab || tab.disabled || (needsAdmin(id) && adminPermissionInsufficient.value)) return;
  router.push({ name: id, params: tab.route_params });
}
</script>

<template>
  <section class="flex min-h-0 flex-col gap-4">
    <div>
      <h1 class="text-xl font-bold">{{ category.title }}</h1>
      <p class="text-base-content/70 mt-1">Chọn một chức năng để xem và thao tác.</p>
    </div>
    <ClientFunctionCatalog :key="category.id" ref="searchInput" :items="items" @select="openFunction" />
  </section>
</template>
